// src/components/kuestion/shared/useAnswerSaver.js
//
// Saves each answer as it is given (backend reply 2, 17 Sep 2026 §3):
//   PUT …/answers/{question_id}  { user_answer, is_flagged, seq }
// One implementation for Solo and for assigned sittings; only the URL differs.
//
// Submit still carries the whole paper, so a failed save never loses work on
// this device. What saving buys is everything beyond this device: a sitting
// resumed elsewhere gets its answers back, the examiner's answered count is
// true, and the server can score a candidate who never returns.
//
// `seq` orders writes: the server ignores a strictly lower one. The rule only
// holds if the counter survives a reload, so it is kept in storage per sitting.
// It starts from the wall clock, which also keeps a second device ahead of the
// first without either knowing the other's count.
//
// SECONDS, not milliseconds. Found live on 17 Sep 2026: the Solo answers table
// stores seq as a 32-bit integer, so 2147483647 saves and 2147483648 is a 500.
// A millisecond clock is ~1.79e12 and failed every write. Seconds fit until
// 2038 and work the same once the column is widened, so they stay.
import { useCallback, useEffect, useRef, useState } from 'react';
import { storageKey } from '../../../config/routes';

const RETRY_MS = 15000;
// After these the sitting takes no more writes; retrying would only be noise.
const FINAL = new Set(['session_already_submitted', 'sitting_already_submitted', 'already_submitted', 'sitting_expired', 'session_not_found', 'sitting_not_found', 'invalid_token',
  // a server that has not deployed the save route yet: say nothing, submit still carries the paper
  'route_not_found', 'method_not_allowed']);

const seqKey = (sittingKey) => storageKey('kuestion', `seq.${sittingKey}`);

export function nextSeq(sittingKey) {
  let last = 0;
  try { last = Number(localStorage.getItem(seqKey(sittingKey))) || 0; } catch { /* private mode */ }
  const seq = Math.max(Math.floor(Date.now() / 1000), last + 1);
  try { localStorage.setItem(seqKey(sittingKey), String(seq)); } catch { /* private mode */ }
  return seq;
}

// On resume the server reports the highest seq it holds (`max_seq`). Starting
// above it makes ordering exact across devices, instead of relying on two
// devices having sane clocks.
export function seedSeq(sittingKey, maxSeq) {
  const floor = Number(maxSeq);
  if (!sittingKey || !Number.isFinite(floor) || floor <= 0) return;
  let last = 0;
  try { last = Number(localStorage.getItem(seqKey(sittingKey))) || 0; } catch { /* private mode */ }
  if (floor > last) { try { localStorage.setItem(seqKey(sittingKey), String(floor)); } catch { /* private mode */ } }
}

export const clearSeq = (sittingKey) => { try { localStorage.removeItem(seqKey(sittingKey)); } catch { /* ignore */ } };

const sig = (answer, flag) => `${answer || ''}|${flag ? 1 : 0}`;

/**
 * @param api         get/putJson set from useKuestionApi or useStudentApi
 * @param urlFor      (questionId) => url, or null while there is no sitting
 * @param sittingKey  stable id of the sitting, for the persisted counter
 * @param answers     { [questionId]: letter }
 * @param flagged     Set of question ids
 * @param enabled     false stops all writes (not running, or submitted)
 * @param baseline    { [questionId]: {user_answer, is_flagged} } the server already holds
 */
export default function useAnswerSaver({ api, urlFor, sittingKey, answers, flagged, enabled, baseline }) {
  const [state, setState] = useState('idle'); // idle | saving | saved | retrying | closed
  const confirmed = useRef({});   // questionId → signature the server has
  const pending = useRef({});     // questionId → signature on the wire right now
  const inFlight = useRef(0);
  const closed = useRef(false);
  const latest = useRef({ answers, flagged });
  latest.current = { answers, flagged };

  useEffect(() => {
    confirmed.current = {};
    pending.current = {};
    closed.current = false;
    Object.entries(baseline || {}).forEach(([qid, a]) => { confirmed.current[qid] = sig(a?.user_answer, a?.is_flagged); });
    setState('idle');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sittingKey]);

  const flush = useCallback(() => {
    if (!enabled || !urlFor || !sittingKey || closed.current) return;
    const { answers: a, flagged: f } = latest.current;
    const ids = new Set([...Object.keys(a), ...[...f].map(String), ...Object.keys(confirmed.current)]);
    ids.forEach((qid) => {
      const answer = a[qid] || '';
      const flag = f.has(qid) || f.has(Number(qid));
      const want = sig(answer, flag);
      if ((confirmed.current[qid] ?? sig('', false)) === want) return;
      if (pending.current[qid] === want) return;
      pending.current[qid] = want;
      inFlight.current += 1;
      setState('saving');
      api.putJson(urlFor(qid), { user_answer: answer, is_flagged: flag, seq: nextSeq(sittingKey) })
        .then(() => { confirmed.current[qid] = want; return true; })
        .catch((e) => { if (FINAL.has(e?.reason)) closed.current = true; return false; })
        .then((ok) => {
          inFlight.current -= 1;
          if (pending.current[qid] === want) delete pending.current[qid];
          if (closed.current) setState('closed');
          else if (!ok) setState('retrying');
          else if (inFlight.current === 0) setState('saved');
        });
    });
  }, [api, urlFor, sittingKey, enabled]);

  // Every change is a write; anything that failed goes again on a timer.
  useEffect(() => { flush(); }, [answers, flagged, flush]);
  useEffect(() => {
    if (!enabled) return undefined;
    const t = setInterval(flush, RETRY_MS);
    return () => clearInterval(t);
  }, [enabled, flush]);

  return state;
}

export const SAVE_LABEL = {
  saving: 'Saving…',
  saved: 'Saved',
  retrying: 'Not saved yet. Still trying. Your answers are kept on this device.',
};
